import { SectionVariants, SectionWidth } from "../../shared";

const sectionsWidth: SectionWidth[] = [480, 600, 800];

function getFreeIndexInArray(result: number[], array: number[], isEven: boolean) {
    let index = 0;
    let reverseIndex = result.length;

    for (let i = 0; i < result.length; i++) {
        if (result[i] === undefined) {
            index = i;
            break;
        }
    }

    for (let i = result.length; i > 0; i--) {
        if (result[i - 1] === undefined) {
            reverseIndex = i;
            break;
        }
    }

    return {
        index: index,
        reverseIndex: reverseIndex - (isEven ? (array.length / 2) : Math.ceil(array.length / 2)),
    };
}

function evenSort(result: number[], array: number[]) {
    const { index, reverseIndex } = getFreeIndexInArray(result, array, true);
    result.splice(index, array.length / 2, ...array.slice(0, array.length / 2));
    result.splice(reverseIndex, array.length / 2, ...array.slice(array.length / 2));
}

function oddSort(result: number[], array: number[]) {
    const { index, reverseIndex } = getFreeIndexInArray(result, array, false);
    result.splice(index, Math.floor(array.length / 2), ...array.slice(0, Math.floor(array.length / 2)));
    result.splice(reverseIndex, Math.ceil(array.length / 2), ...array.slice(Math.floor(array.length / 2)));
}

function mirrorSort(numbers: number[], sectionsWidth: SectionWidth[]): number[] {
    const result: number[] = new Array(numbers.length);
    const values: { [key: number]: number[] } = {};

    for (const sectionWidth of Object.values(sectionsWidth)) {
        values[sectionWidth] = numbers.filter((value) => value === sectionWidth);
    }

    const keys = Object.keys(values).map(Number).sort((a, b) => b - a);

    for (const key of keys) {
        const elements = values[key];
        if (elements.length % 2 === 0) {
            evenSort(result, elements);
        }
    }

    for (const key of keys) {
        const elements = values[key];
        if (elements.length % 2 === 1) {
            oddSort(result, elements);
        }
    }

    return result;
}

function generationVariants(totalWidth: number, sectionsWidth: SectionWidth[]): SectionVariants {
    let gVariants: SectionVariants = {};

    for (const sectionWidth of Object.values(sectionsWidth.sort((a, b) => b - a))) {
        if (totalWidth < sectionWidth) {
            gVariants[totalWidth] = null;
        } else {
            gVariants[sectionWidth] = generationVariants(totalWidth - sectionWidth, sectionsWidth);
        }
    }

    return gVariants;
}

function conversionVariants(variants: SectionVariants, maxRest: number, acc: SectionWidth[] = []): SectionWidth[][]  {
    let total: SectionWidth[][] = [];

    for (const [key, value] of Object.entries(variants)) {
        if (value === null) {
            if (Number(key) <= maxRest) {
                total.push([...acc]);
            }
        } else {
            const nestedResults = conversionVariants(value, maxRest, [...acc, Number(key)])
            total = total.concat(nestedResults);
        }
    }

    return total;
}

function sortingVariants(variants: SectionWidth[][], sectionsWidth: SectionWidth[]): SectionWidth[][]  {
    return variants.map((array, index) => {
        return mirrorSort(variants[index], sectionsWidth);
    });
}

function filteringVariants(variants: SectionWidth[][]): SectionWidth[][]  {
    return variants.filter((array, index, self) =>
        index === self.findIndex((arr) =>
            arr.length === array.length && arr.every((value, i) => value === array[i])
        )
    );
}

export function getAutoFillingVariants(totalWidth: number, maxRest: number = 200): SectionWidth[][] {
    const gVariants = generationVariants(totalWidth, sectionsWidth);
    const cVariants = conversionVariants(gVariants, maxRest);
    const sVariants = sortingVariants(cVariants, sectionsWidth);
    const fVariants = filteringVariants(sVariants);
    const variants = fVariants.sort((a, b) =>
        a.reduce((k, p) => k + p, 0) < b.reduce((k, p) => k + p, 0) ? 1 : -1
    );

    return variants;
}